/**
 * Evidence — aggregated list of finding-linked evidence.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, EvidenceListItem } from '../lib/api'
import { LoadingSpinner } from '../components/design-system/Loading'
import { EmptyState } from '../components/design-system/EmptyState'
import { Button } from '../components/design-system/Button'
import { SeverityBadge } from '../components/design-system/Badge'
import { useLocale } from '../contexts/LocaleContext'
import { evidenceTypeLabel } from '../lib/i18n'

export function EvidencePage() {
  const { t, locale } = useLocale()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [items, setItems] = useState<EvidenceListItem[]>([])
  const [total, setTotal] = useState(0)
  const search = searchParams.get('search') || ''
  const auditRunId = searchParams.get('audit_run_id') || ''
  const page = parseInt(searchParams.get('page') || '1', 10)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        setError(null)
        const data = await api.listEvidence({
          search: search || undefined,
          audit_run_id: auditRunId || undefined,
          page,
          page_size: 50,
        })
        if (!cancelled) {
          setItems(data.items)
          setTotal(data.total)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('evidence.failed'))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [search, page, auditRunId, t])

  if (loading) {
    return (
      <div data-testid="evidence-page" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <h1 style={h1Style}>{t('evidence.title')}</h1>
        <div style={{ display: 'flex', justifyContent: 'center', minHeight: '240px', alignItems: 'center' }}>
          <LoadingSpinner size="lg" text={t('evidence.loading')} />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card card-error" data-testid="evidence-page">
        <h1 style={h1Style}>{t('evidence.title')}</h1>
        <p className="text-sm">{error}</p>
        <Button onClick={() => window.location.reload()}>{t('common.retry')}</Button>
      </div>
    )
  }

  return (
    <div data-testid="evidence-page" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <header>
        <h1 style={h1Style}>{t('evidence.title')}</h1>
        <p style={subStyle}>
          {total === 1 ? t('evidence.countOne') : t('evidence.count', { n: total })}
        </p>
      </header>
      <input
        type="search"
        placeholder={t('evidence.searchPh')}
        defaultValue={search}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const value = (e.target as HTMLInputElement).value
            const next = new URLSearchParams(searchParams)
            if (value) next.set('search', value)
            else next.delete('search')
            next.delete('page')
            setSearchParams(next)
          }
        }}
        style={inputStyle}
        aria-label={t('evidence.searchAria')}
      />
      {items.length === 0 ? (
        <EmptyState
          title={t('evidence.empty')}
          description={t('evidence.emptyBody')}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {items.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(`/finding/${item.finding_id}`)}
              style={cardButtonStyle}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                <strong>{item.finding_title}</strong>
                <SeverityBadge severity={item.finding_severity} />
              </div>
              <div className="text-sm" style={{ color: 'var(--reviewer-text-secondary)' }}>
                {item.project_name} · {evidenceTypeLabel(locale, item.evidence_type)} · {item.description}
              </div>
              {item.content_preview && (
                <pre style={previewStyle}>{item.content_preview}</pre>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const h1Style = {
  margin: 0,
  marginBottom: '8px',
  fontFamily: 'var(--reviewer-font-mono)',
  fontSize: 'var(--reviewer-text-2xl)',
  fontWeight: 500,
} as const

const subStyle = {
  margin: 0,
  fontFamily: 'var(--reviewer-font-mono)',
  color: 'var(--reviewer-text-secondary)',
} as const

const inputStyle = {
  padding: '8px 12px',
  fontFamily: 'var(--reviewer-font-mono)',
  backgroundColor: 'var(--reviewer-bg)',
  border: '1px solid var(--reviewer-border)',
  color: 'var(--reviewer-text)',
} as const

const cardButtonStyle = {
  textAlign: 'left' as const,
  padding: '16px',
  border: '1px solid var(--reviewer-border)',
  backgroundColor: 'var(--reviewer-surface)',
  color: 'var(--reviewer-text)',
  cursor: 'pointer',
  fontFamily: 'var(--reviewer-font-mono)',
}

const previewStyle = {
  marginTop: '10px',
  padding: '10px',
  backgroundColor: 'var(--reviewer-bg)',
  fontSize: '12px',
  overflowX: 'auto' as const,
  whiteSpace: 'pre-wrap' as const,
}
